import React from 'react';
import styles from './InspectionChecklist.module.scss';
import { useTranslation } from 'react-i18next';
import { Switch, styled } from '@mui/material';
import { DynamicComponentProps } from '@/types/ModuleComponentProp.type';
import { useCheckpointChecks } from '@/hooks/masterData';
import {
  getActiveChecksForCheckpoint,
  getCheckpointCheckTranslationKey,
  isCheckpointCheckChecked,
} from '@/utils/checkpoint-checks.util';

const AntSwitch = styled(Switch)(({ theme }) => ({
  width: 48,
  height: 24,
  padding: 0,
  display: 'flex',
  '&:active': {
    '& .MuiSwitch-thumb': {
      width: 20,
    },
    '& .MuiSwitch-switchBase.Mui-checked': {
      transform: 'translateX(24px)',
    },
  },
  '& .MuiSwitch-switchBase': {
    padding: 1,
    '&.Mui-checked': {
      transform: 'translateX(24px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        opacity: 1,
        backgroundColor: '#1976D2',
        ...theme.applyStyles('dark', {
          backgroundColor: '#1976D2',
        }),
      },
    },
  },
  '& .MuiSwitch-thumb': {
    boxShadow: '0 2px 4px 0 rgb(0 35 11 / 20%)',
    width: 20,
    height: 20,
    borderRadius: 10,
    transition: theme.transitions.create(['width'], {
      duration: 200,
    }),
  },
  '& .MuiSwitch-track': {
    borderRadius: 24 / 2,
    opacity: 1,
    backgroundColor: 'rgba(0,0,0,.25)',
    boxSizing: 'border-box',
    ...theme.applyStyles('dark', {
      backgroundColor: 'rgba(255,255,255,.35)',
    }),
  },
}));

export const CheckpointChecks: React.FC<DynamicComponentProps> = ({
  inspection,
  structure,
  moduleName,
  onCheckpointCheckChange,
  isChecklistInvalid = false,
}) => {
  const { t } = useTranslation();
  const { checkpointChecks } = useCheckpointChecks();
  const checkpointCode = moduleName || structure.name;
  const templateId = inspection.template?.id;
  const checks = getActiveChecksForCheckpoint(checkpointChecks, templateId, checkpointCode);
  return (
    <div className={styles.formContainer}>
      <h2 className={styles.header}>{t(structure.header || 'Checks')}</h2>
      <div className={styles.checksRow}>
        {checks.map((check) => {
          const labelKey = getCheckpointCheckTranslationKey(check);
          const isChecked = isCheckpointCheckChecked(inspection.checkpointCheckResults, check.id);
          return (
            <div className={styles.inputGroup} key={check.id}>
              <p className={styles.heading}>{t(labelKey)}</p>
              <div
                className={`${styles.switchWrap}${isChecklistInvalid && check.isMandatory && !isChecked ? ` ${styles.switchWrapInvalid}` : ''}`}
              >
                <AntSwitch
                  checked={isChecked}
                  onChange={(event) => onCheckpointCheckChange?.(check.id, event.target.checked)}
                  slotProps={{
                    input: {
                      'aria-label': t(labelKey),
                      'aria-invalid': isChecklistInvalid && check.isMandatory && !isChecked,
                    },
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
